import React, { createContext, useContext, useState, useEffect } from 'react';

const CompanyProfileContext = createContext();

export const useCompanyProfile = () => {
  const context = useContext(CompanyProfileContext);
  if (!context) {
    throw new Error('useCompanyProfile must be used within a CompanyProfileProvider');
  }
  return context;
};

export const CompanyProfileProvider = ({ children }) => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch company profile from backend
  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/company/get-company-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyProfile(data);
      } else if (response.status === 404) {
        // No company profile found - this is okay
        setCompanyProfile(null);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if Google Sheets API is configured
  const hasGoogleSheetsAPI = () => {
    return companyProfile && companyProfile.gsapi && companyProfile.gsapi.trim() !== '';
  };

  // Get Google Sheets API URL
  const getGoogleSheetsAPI = () => {
    return companyProfile?.gsapi || '';
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const value = {
    companyProfile,
    loading,
    error,
    fetchCompanyProfile,
    hasGoogleSheetsAPI,
    getGoogleSheetsAPI,
  };

  return (
    <CompanyProfileContext.Provider value={value}>
      {children}
    </CompanyProfileContext.Provider>
  );
};
